import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { Compass, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { useCurrentUser, usePredictionOperator } from '@/auth/useSession';
import { useTour } from '@/features/tour/context';
import { canSee, NAV, screenOf } from './nav';
import { setThemePreference, type ThemePreference } from './theme';

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
};

const itemClass =
  'flex cursor-default items-center gap-2.5 rounded-ctl px-2.5 py-2 text-sm outline-none select-none data-[selected=true]:bg-wash [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted';
const groupClass = '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted';

/** ⌘K: jump to any screen you can see, switch theme, log out. */
export function CommandPalette({ open, onOpenChange, onLogout }: CommandPaletteProps) {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const operator = usePredictionOperator();
  const access = { role: me.data?.role, isPredictionOperator: operator.data === true };
  const screens = NAV.flatMap((g) => g.items).filter((item) => canSee(item, access));
  const tour = useTour();
  const here = tour.screen;

  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };
  const theme = (value: ThemePreference, label: string, icon: ReactNode) => (
    <Command.Item value={`Theme ${label}`} onSelect={() => { run(() => { setThemePreference(value); }); }} className={itemClass}>
      {icon}
      Theme: {label}
    </Command.Item>
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed top-[12vh] left-1/2 z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-pop bg-surface text-fg shadow-pop outline-none data-[state=open]:animate-pop-in"
        >
          <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
          <Command loop label="Search screens and actions">
            <div className="flex items-center gap-2 border-b border-line px-3.5">
              <Search className="size-4 shrink-0 text-muted" aria-hidden="true" />
              <Command.Input placeholder="Go to a screen or run an action…" className="h-12 w-full border-0 bg-transparent text-base outline-none placeholder:text-faint focus-visible:shadow-none" />
            </div>
            <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-8 text-center text-sm text-muted">No matching screen or action.</Command.Empty>
              <Command.Group heading="Go to" className={groupClass}>
                {screens.map((item) => {
                  const screen = screenOf(item);
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={item.screen}
                      value={screen.title}
                      onSelect={() => { run(() => { void navigate({ to: screen.path }); }); }}
                      className={itemClass}
                    >
                      <Icon aria-hidden="true" strokeWidth={1.5} />
                      {screen.title}
                    </Command.Item>
                  );
                })}
              </Command.Group>
              <Command.Group heading="Actions" className={groupClass}>
                <Command.Item value="Take the tour" onSelect={() => { run(() => { tour.start('all'); }); }} className={itemClass}>
                  <Compass aria-hidden="true" strokeWidth={1.5} />
                  Take the tour
                </Command.Item>
                {here ? (
                  <Command.Item value="Tour this screen" onSelect={() => { run(() => { tour.start(here); }); }} className={itemClass}>
                    <Compass aria-hidden="true" strokeWidth={1.5} />
                    Tour this screen
                  </Command.Item>
                ) : null}
                {theme('system', 'System', <Monitor aria-hidden="true" />)}
                {theme('light', 'Light', <Sun aria-hidden="true" />)}
                {theme('dark', 'Dark', <Moon aria-hidden="true" />)}
                <Command.Item value="Log out" onSelect={() => { run(onLogout); }} className={itemClass}>
                  <LogOut aria-hidden="true" />
                  Log out
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
